import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'api/src/types'
import { useCallback, useEffect, useRef, useState } from 'react'

import { browserNotificationRuntime } from '@/lib/browser-notification-runtime'
import type { BrowserNotificationRuntime } from '@/lib/browser-notification-runtime'
import { useTRPC } from '@/lib/trpc'
import {
  adoptWebPushSubscriptionActivation,
  captureWebPushInstallationBinding,
  getOrCreateWebPushInstallationId,
  invalidateWebPushSubscriptionAdoption,
  serializePushSubscription,
  disableWebPushCurrentDevice as disableBoundWebPushDevice,
  WEB_PUSH_IDENTITY_REVISION_STORAGE_KEY,
  webPushInstallationBindingIsCurrent,
} from '@/lib/web-push-client'
import {
  canDisableCurrentWebPushDevice,
  canExplicitlyEnableWebPush,
  isActiveWebPushServerState,
  type LocalPushSubscriptionState,
  resolveWebPushState,
  type WebPushUiState,
} from '@/lib/web-push-state'
import { enableWebPushForCurrentDevice } from '@/lib/web-push-activation'

type WebPushStatus = inferRouterOutputs<AppRouter>['webPushRouter']['status']

interface MutableValue<T> {
  current: T
}

function claimValue<T>(slot: MutableValue<T | null>, value: T): boolean {
  if (slot.current === value)
    return false
  slot.current = value
  return true
}

function clearClaimedValue<T>(slot: MutableValue<T | null>, value: T): void {
  if (slot.current === value)
    slot.current = null
}

function clearValue<T>(slot: MutableValue<T | null>): void {
  slot.current = null
}

export type WebPushPreferenceCategory
  = | 'global'
    | 'game'
    | 'friends'
    | 'system'

export interface WebPushViewModel {
  state: WebPushUiState
  statePending: boolean
  serviceAvailable: boolean
  preferences: {
    global: boolean
    game: boolean
    friends: boolean
    system: boolean
  }
  statusPending: boolean
  preferencesPending: boolean
  preferencesLoadError: boolean
  actionPending: boolean
  preferencePending: WebPushPreferenceCategory | null
  actionError: boolean
  preferenceError: boolean
  enable: () => Promise<void>
  disableCurrentDevice: () => Promise<void>
  setPreference: (
    category: WebPushPreferenceCategory,
    enabled: boolean,
  ) => void
  canEnable: boolean
  canDisableCurrentDevice: boolean
}

export function useWebPush(
  expectedUserId: string,
  expectedSessionId: string,
  runtime: BrowserNotificationRuntime = browserNotificationRuntime,
): WebPushViewModel {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [installationId, setInstallationId] = useState(() =>
    getOrCreateWebPushInstallationId(),
  )
  const [browserRevision, setBrowserRevision] = useState(0)
  const [localSubscription, setLocalSubscription]
    = useState<LocalPushSubscriptionState>('checking')
  const [actionPending, setActionPending] = useState(false)
  const [actionError, setActionError] = useState(false)
  const passiveRefreshEndpoint = useRef<string | null>(null)
  const passiveDeactivation = useRef<string | null>(null)
  const preferenceWriteInFlight = useRef(false)

  const statusOptions = trpc.webPushRouter.status.queryOptions({
    expectedUserId,
    expectedSessionId,
    installationId,
  })
  const preferencesOptions = trpc.webPushRouter.preferences.queryOptions({
    expectedUserId,
    expectedSessionId,
  })
  const statusQuery = useQuery({
    ...statusOptions,
    retry: 1,
    refetchOnWindowFocus: true,
  })
  const preferencesQuery = useQuery({
    ...preferencesOptions,
    retry: 1,
    refetchOnWindowFocus: true,
  })

  const cacheStatus = useCallback(
    (next: WebPushStatus) => {
      queryClient.setQueryData(statusOptions.queryKey, next)
    },
    [queryClient, statusOptions.queryKey],
  )

  const registerMutation = useMutation(
    trpc.webPushRouter.register.mutationOptions(),
  )
  const refreshMutation = useMutation({
    ...trpc.webPushRouter.refresh.mutationOptions(),
    onSuccess: cacheStatus,
  })
  const deactivateMutation = useMutation({
    ...trpc.webPushRouter.deactivate.mutationOptions(),
    onSuccess: cacheStatus,
  })
  const preferenceMutation = useMutation({
    ...trpc.webPushRouter.setPreference.mutationOptions(),
    onSuccess: (next) => {
      queryClient.setQueryData(preferencesOptions.queryKey, next)
    },
  })
  const refreshSubscription = refreshMutation.mutateAsync
  const deactivateSubscription = deactivateMutation.mutateAsync
  const setPreferenceAsync = preferenceMutation.mutateAsync
  const refreshSubscriptionRef = useRef(refreshSubscription)
  const deactivateSubscriptionRef = useRef(deactivateSubscription)
  const setPreferenceAsyncRef = useRef(setPreferenceAsync)
  const refetchStatusRef = useRef(statusQuery.refetch)
  const refetchPreferencesRef = useRef(preferencesQuery.refetch)
  const cacheStatusRef = useRef(cacheStatus)
  refreshSubscriptionRef.current = refreshSubscription
  deactivateSubscriptionRef.current = deactivateSubscription
  setPreferenceAsyncRef.current = setPreferenceAsync
  refetchStatusRef.current = statusQuery.refetch
  refetchPreferencesRef.current = preferencesQuery.refetch
  cacheStatusRef.current = cacheStatus

  const fetchAuthoritativeStatus = useCallback(
    async (currentInstallationId: string) => {
      const probeQueryClient = new QueryClient({
        defaultOptions: { queries: { retry: false } },
      })
      try {
        return await probeQueryClient.fetchQuery(
          trpc.webPushRouter.status.queryOptions({
            expectedUserId,
            expectedSessionId,
            installationId: currentInstallationId,
          }),
        )
      }
      finally {
        probeQueryClient.clear()
      }
    },
    [expectedSessionId, expectedUserId, trpc.webPushRouter.status],
  )

  const capabilities = runtime.readCapabilities()
  const permission = runtime.notificationPermission()
  const { platform } = runtime.readInstallationHints()
  const serverState = statusQuery.data?.subscriptionState
  const hasServerStatus = !!statusQuery.data
  const state = resolveWebPushState({
    capabilities,
    platform,
    permission,
    serverState,
    localSubscription,
    serviceAvailable: statusQuery.data?.serviceAvailable,
    serverInspectionFailed: statusQuery.isError,
  })

  useEffect(() => {
    const refreshBrowserTruth = () => {
      setBrowserRevision(value => value + 1)
      void refetchStatusRef.current()
      void refetchPreferencesRef.current()
    }
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible')
        refreshBrowserTruth()
    }
    const refreshAfterSiblingLifecycle = (event: StorageEvent) => {
      if (event.key === WEB_PUSH_IDENTITY_REVISION_STORAGE_KEY)
        refreshBrowserTruth()
    }
    window.addEventListener('focus', refreshBrowserTruth)
    window.addEventListener('storage', refreshAfterSiblingLifecycle)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.removeEventListener('focus', refreshBrowserTruth)
      window.removeEventListener('storage', refreshAfterSiblingLifecycle)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [])

  useEffect(() => {
    setInstallationId(getOrCreateWebPushInstallationId())
    passiveRefreshEndpoint.current = null
    passiveDeactivation.current = null
    setLocalSubscription('checking')
  }, [browserRevision, expectedSessionId, expectedUserId, installationId])

  useEffect(() => {
    if (statusQuery.isFetching || statusQuery.isError || !hasServerStatus)
      return
    let cancelled = false
    if (
      (platform === 'ios' && !capabilities.standalone)
      || !capabilities.serviceWorker
      || !capabilities.push
      || !capabilities.notification
      || !capabilities.lifecycleLock
      || permission === 'unsupported'
    ) {
      setLocalSubscription('missing')
      return
    }

    const capturedBinding = captureWebPushInstallationBinding()
    const lifecycleIdentityIsCurrent = (
      binding = capturedBinding,
    ) =>
      !cancelled
      && binding.installationId === installationId
      && webPushInstallationBindingIsCurrent(binding)

    const deactivateForRevokedPermission = async (
      currentPermission: Exclude<
        ReturnType<typeof runtime.notificationPermission>,
        'granted'
      >,
      currentServerState: typeof serverState,
    ) => {
      if (
        !lifecycleIdentityIsCurrent()
        || !isActiveWebPushServerState(currentServerState)
        || !capturedBinding.bindingToken
      ) {
        return
      }
      if (!claimValue(passiveDeactivation, currentPermission))
        return
      try {
        await deactivateSubscriptionRef.current({
          expectedUserId,
          expectedSessionId,
          installationId,
          bindingToken: capturedBinding.bindingToken,
          reason: 'permission_revoked',
        })
        if (!lifecycleIdentityIsCurrent())
          return
        const invalidatedBinding
          = invalidateWebPushSubscriptionAdoption(capturedBinding)
        if (!invalidatedBinding)
          throw new Error('Push subscription adoption changed')
        await runtime.unsubscribeFromPush()
        if (!lifecycleIdentityIsCurrent(invalidatedBinding))
          return
        await runtime.closeWebPushNotifications().catch(() => {})
      }
      catch (error) {
        clearClaimedValue(passiveDeactivation, currentPermission)
        throw error
      }
    }

    const deactivateForMissingSubscription = async (
      currentServerState: typeof serverState,
    ) => {
      const operation = 'subscription-missing'
      if (
        !lifecycleIdentityIsCurrent()
        || !isActiveWebPushServerState(currentServerState)
        || !capturedBinding.bindingToken
        || !claimValue(passiveDeactivation, operation)
      ) {
        return
      }
      try {
        await deactivateSubscriptionRef.current({
          expectedUserId,
          expectedSessionId,
          installationId,
          bindingToken: capturedBinding.bindingToken,
          reason: 'subscription_missing',
        })
        if (!lifecycleIdentityIsCurrent())
          return
        const invalidatedBinding
          = invalidateWebPushSubscriptionAdoption(capturedBinding)
        if (!invalidatedBinding)
          throw new Error('Push subscription adoption changed')
        if (!lifecycleIdentityIsCurrent(invalidatedBinding))
          return
        await runtime.closeWebPushNotifications().catch(() => {})
      }
      catch (error) {
        clearClaimedValue(passiveDeactivation, operation)
        throw error
      }
    }

    const refreshCurrentSubscription = async (
      currentServerState: typeof serverState,
      subscription: PushSubscription,
    ) => {
      if (
        !lifecycleIdentityIsCurrent()
        || !isActiveWebPushServerState(currentServerState)
        || capturedBinding.ownerUserId !== expectedUserId
        || capturedBinding.ownerSessionId !== expectedSessionId
        || !capturedBinding.bindingToken
        || !claimValue(passiveRefreshEndpoint, subscription.endpoint)
      ) {
        return
      }
      try {
        await refreshSubscriptionRef.current({
          expectedUserId,
          expectedSessionId,
          installationId,
          bindingToken: capturedBinding.bindingToken,
          ...serializePushSubscription(subscription),
        })
        if (!lifecycleIdentityIsCurrent())
          return
        if (capturedBinding.subscriptionGeneration) {
          const adoptedBinding = await adoptWebPushSubscriptionActivation(
            capturedBinding,
            subscription.endpoint,
          )
          if (!adoptedBinding)
            throw new Error('Push subscription adoption changed')
        }
      }
      catch (error) {
        clearClaimedValue(passiveRefreshEndpoint, subscription.endpoint)
        throw error
      }
    }

    if (permission !== 'granted') {
      setLocalSubscription('missing')
      if (
        isActiveWebPushServerState(serverState)
        && !!capturedBinding.bindingToken
      ) {
        void runtime
          .withWebPushLifecycleLock(async () => {
            const freshStatus = await fetchAuthoritativeStatus(installationId)
            if (lifecycleIdentityIsCurrent())
              cacheStatusRef.current(freshStatus)
            const currentPermission = runtime.notificationPermission()
            if (currentPermission === 'granted')
              return
            await deactivateForRevokedPermission(
              currentPermission,
              freshStatus.subscriptionState,
            )
          })
          .catch(() => {})
      }
      return () => {
        cancelled = true
      }
    }

    setLocalSubscription('checking')
    void runtime
      .withWebPushLifecycleLock(async () => {
        const freshStatus = await fetchAuthoritativeStatus(installationId)
        if (!lifecycleIdentityIsCurrent())
          return
        cacheStatusRef.current(freshStatus)
        const currentServerState = freshStatus.subscriptionState
        const currentPermission = runtime.notificationPermission()
        if (currentPermission !== 'granted') {
          setLocalSubscription('missing')
          await deactivateForRevokedPermission(
            currentPermission,
            currentServerState,
          )
          return
        }
        const subscription = await runtime.getPushSubscription()
        if (!lifecycleIdentityIsCurrent())
          return
        if (!subscription) {
          setLocalSubscription('missing')
          await deactivateForMissingSubscription(currentServerState)
          return
        }

        setLocalSubscription('present')
        if (currentServerState === 'active')
          clearValue(passiveDeactivation)
        await refreshCurrentSubscription(currentServerState, subscription)
      })
      .catch(() => {
        if (!cancelled)
          setLocalSubscription('error')
      })

    return () => {
      cancelled = true
    }
  }, [
    browserRevision,
    capabilities.notification,
    capabilities.lifecycleLock,
    capabilities.push,
    capabilities.serviceWorker,
    capabilities.standalone,
    expectedSessionId,
    expectedUserId,
    fetchAuthoritativeStatus,
    installationId,
    permission,
    platform,
    runtime,
    hasServerStatus,
    statusQuery.isError,
    statusQuery.isFetching,
    serverState,
  ])

  const enable = useCallback(async () => {
    await enableWebPushForCurrentDevice({
      expectedUserId,
      expectedSessionId,
      runtime,
      fetchPublicKey: async () =>
        (
          await queryClient.fetchQuery(
            trpc.webPushRouter.publicKey.queryOptions({
              expectedUserId,
              expectedSessionId,
            }),
          )
        ).publicKey,
      fetchStatus: fetchAuthoritativeStatus,
      register: input => registerMutation.mutateAsync(input),
      deactivate: input => deactivateSubscriptionRef.current(input),
      cacheStatus: (activationInstallationId, next) =>
        queryClient.setQueryData(
          trpc.webPushRouter.status.queryOptions({
            expectedUserId,
            expectedSessionId,
            installationId: activationInstallationId,
          }).queryKey,
          next,
        ),
      setInstallationId,
      setLocalSubscription,
      setActionError,
      setActionPending,
      notifyBrowserTruthChanged: () => setBrowserRevision(value => value + 1),
      requestPassiveReconciliation: () =>
        setBrowserRevision(value => value + 1),
      setPassiveRefreshEndpoint: (endpoint) => {
        passiveRefreshEndpoint.current = endpoint
      },
      clearPassiveDeactivation: () => {
        passiveDeactivation.current = null
      },
    })
  }, [
    expectedSessionId,
    expectedUserId,
    fetchAuthoritativeStatus,
    queryClient,
    registerMutation,
    runtime,
    trpc.webPushRouter.publicKey,
    trpc.webPushRouter.status,
  ])

  const disableCurrentDevice = useCallback(async () => {
    setActionPending(true)
    setActionError(false)
    try {
      const result = await disableBoundWebPushDevice(
        expectedUserId,
        expectedSessionId,
        { deactivate: deactivateSubscription },
        runtime,
      )
      setLocalSubscription(
        result.localSubscriptionRemoved ? 'missing' : 'error',
      )
      clearValue(passiveRefreshEndpoint)
      setActionError(
        !result.serverDeactivated
        || !result.localSubscriptionRemoved
        || result.staleIdentity,
      )
    }
    catch {
      setLocalSubscription('error')
      setActionError(true)
    }
    finally {
      setActionPending(false)
    }
  }, [deactivateSubscription, expectedSessionId, expectedUserId, runtime])

  const setPreference = useCallback(
    (category: WebPushPreferenceCategory, enabled: boolean) => {
      if (preferenceWriteInFlight.current)
        return
      preferenceWriteInFlight.current = true
      void setPreferenceAsyncRef
        .current({
          expectedUserId,
          expectedSessionId,
          category,
          enabled,
        })
        .catch(() => {})
        .finally(() => {
          preferenceWriteInFlight.current = false
        })
    },
    [expectedSessionId, expectedUserId],
  )

  return {
    state,
    statePending:
      statusQuery.isPending
      || (
        !statusQuery.isError
        && hasServerStatus
        && localSubscription === 'checking'
      ),
    serviceAvailable: statusQuery.data?.serviceAvailable ?? false,
    preferences: preferencesQuery.data ?? {
      global: true,
      game: true,
      friends: true,
      system: true,
    },
    statusPending: statusQuery.isPending,
    preferencesPending: preferencesQuery.isPending,
    preferencesLoadError: preferencesQuery.isError,
    actionPending,
    preferencePending: preferenceMutation.isPending
      ? (preferenceMutation.variables?.category ?? null)
      : null,
    actionError,
    preferenceError: preferenceMutation.isError,
    enable,
    disableCurrentDevice,
    setPreference,
    canEnable:
      canExplicitlyEnableWebPush(state) && !!statusQuery.data?.serviceAvailable,
    canDisableCurrentDevice: canDisableCurrentWebPushDevice(state),
  }
}
